/**
 * Fetch wrapper: prepends no path (callers pass full /api/...), injects the
 * Teams SSO Bearer token when available, and throws typed ApiError on !2xx.
 */

import { getAuthToken, initializeTeams } from "./teamsClient";

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = "ApiError";
  }
}

/**
 * Pull a human-readable message out of a FastAPI error body.
 *
 * FastAPI emits ``{"detail": "..."}`` for HTTPException, and the Symphony
 * push route nests structured payloads as ``{"detail": {"detail": "..."}}``.
 * Falls back to ``null`` for non-string bodies so the caller can use the
 * HTTP status line.
 */
function extractDetailMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const detail = (body as Record<string, unknown>).detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const inner = (detail as Record<string, unknown>).detail;
    if (typeof inner === "string") return inner;
  }
  return null;
}

let requireAuthPromise: Promise<boolean> | null = null;

async function requireAuth(): Promise<boolean> {
  if (!requireAuthPromise) {
    requireAuthPromise = (async () => {
      const res = await fetch("/api/config");
      if (!res.ok) return true;
      const body = (await res.json()) as { requireAuth?: boolean };
      return body.requireAuth ?? true;
    })();
  }
  return requireAuthPromise;
}

async function authHeader(): Promise<Record<string, string>> {
  const inTeams = await initializeTeams();
  if (!inTeams) return {};
  const needed = await requireAuth();
  if (!needed) return {};
  try {
    const token = await getAuthToken();
    return { Authorization: `Bearer ${token}` };
  } catch {
    return {};
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const auth = await authHeader();
  const headers: HeadersInit = {
    Accept: "application/json",
    ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...auth,
    ...(options.headers ?? {}),
  };
  const res = await fetch(path, {
    ...options,
    headers,
    signal: options.signal,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    // Prefer the API's ``detail`` text over the bland HTTP status line so
    // validation errors (e.g. trade-key format) reach the operator. The
    // raw body is still available via ``ApiError.body`` for callers that
    // need structured fields (Symphony 502s, etc.).
    const message = extractDetailMessage(body) ?? `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, message, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiClient = {
  get: <T>(path: string, init?: RequestInit) => request<T>(path, init),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body }),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body }),
  async blob(path: string): Promise<string> {
    const auth = await authHeader();
    const res = await fetch(path, { headers: auth });
    if (!res.ok) throw new ApiError(res.status, res.statusText, null);
    return URL.createObjectURL(await res.blob());
  },
};
